import {
  ButtonDS,
  EditOutlined,
  SendOutlined,
  LaunchPage,
  MaterialUiColumnsProps,
  MaterialUiTable,
  Spacer,
  TextCapitalized,
  usePersistMuiTableState,
  triggerToast,
} from "@qivia/ui";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import styled from "styled-components";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import {
  companiesSepaDirectDebitDataAsync,
  companiesSepaDirectDebitSlice,
  selectActivateLimitBalanceDirectDebitStatus,
  selectActivateStatementDirectDebitStatus,
  selectCompaniesSepaDirectDebitData,
  selectCompaniesSepaDirectDebitDataStatus,
  selectCreateSepaDirectDebitStatus,
  selectDeactivateDirectDebitStatus,
  selectUpdateLimitBalanceDirectDebitStatus,
  selectUpdateStatementDirectDebitStatus,
  selectUpdateUsageFeesDirectDebitStatus,
} from "./sepaDirectDebitSlice";
import {
  SepaDirectDebitDataDisplayed,
  CompanySepaDirectDebitDataList,
} from "./sepaDirectDebitApi";
import { selectAdminUser } from "../homeSlice";
import { ChangeSepaDirectDebitTypeModal } from "./modals/changeSepaDirectDebitTypeModal";
import { CreateSepaDirectDebitModal } from "./modals/createSepaDirectDebitModal";
import { UpdateSepaDirectDebitOptionsTypeModal } from "./modals/updateSepaDirectDebitOptionsTypeModal";
import { UsageFeesDirectDebitCell } from "./cells/usageFeesDirectDebitCell";

export const SepaDirectDebitTab = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const params = useParams();
  const sepaDirectDebitList = useAppSelector(
    selectCompaniesSepaDirectDebitData,
  );
  const createSepaDirectDebitStatus = useAppSelector(
    selectCreateSepaDirectDebitStatus,
  );
  const updateLimitBalanceDirectDebitStatus = useAppSelector(
    selectUpdateLimitBalanceDirectDebitStatus,
  );
  const updateStatementDirectDebitStatus = useAppSelector(
    selectUpdateStatementDirectDebitStatus,
  );
  const sepaDirectDebitDataStatus = useAppSelector(
    selectCompaniesSepaDirectDebitDataStatus,
  );
  const activateStatementDirectDebitStatus = useAppSelector(
    selectActivateStatementDirectDebitStatus,
  );
  const activateLimitBalanceDirectDebitStatus = useAppSelector(
    selectActivateLimitBalanceDirectDebitStatus,
  );
  const deactivateDirectDebitStatus = useAppSelector(
    selectDeactivateDirectDebitStatus,
  );
  const updateUsageFeesDirectDebitStatus = useAppSelector(
    selectUpdateUsageFeesDirectDebitStatus,
  );
  const [listDisplayed, setListDisplayed] = useState<
    SepaDirectDebitDataDisplayed[] | null
  >(null);
  const adminUser = useAppSelector(selectAdminUser);
  const [
    currentSepaDirectDebitCompanyUuid,
    setCurrentSepaDirectDebitCompanyUuid,
  ] = useState<string | null>(null);
  const [
    createSepaDirectDebitModalVisible,
    setCreateSepaDirectDebitModalVisible,
  ] = useState<boolean>(false);
  const [
    changeSepaDirectDebitTypeModalVisible,
    setChangeSepaDirectDebitTypeModalVisible,
  ] = useState<boolean>(false);
  const [
    updateSepaDirectDebitOptionsTypeModalVisible,
    setUpdateSepaDirectDebitOptionsTypeModalVisible,
  ] = useState<boolean>(false);

  useEffect(() => {
    if (createSepaDirectDebitStatus === "success") {
      triggerToast(t("createSepaDirectDebitStatus.success") || "", "valid");
      dispatch(
        companiesSepaDirectDebitSlice.actions.resetCreateSepaDirectDebitStatus(),
      );
    } else if (createSepaDirectDebitStatus === "failed") {
      triggerToast(t("createSepaDirectDebitStatus.failure") || "", "error");
      dispatch(
        companiesSepaDirectDebitSlice.actions.resetCreateSepaDirectDebitStatus(),
      );
    }
  }, [createSepaDirectDebitStatus, t, dispatch]);

  useEffect(() => {
    if (updateLimitBalanceDirectDebitStatus === "success") {
      triggerToast(
        t("updateLimitBalanceDirectDebitStatus.success") || "",
        "valid",
      );
      dispatch(
        companiesSepaDirectDebitSlice.actions.resetUpdateLimitBalanceDirectDebitStatus(),
      );
    } else if (updateLimitBalanceDirectDebitStatus === "failed") {
      triggerToast(
        t("updateLimitBalanceDirectDebitStatus.failure") || "",
        "error",
      );
      dispatch(
        companiesSepaDirectDebitSlice.actions.resetUpdateLimitBalanceDirectDebitStatus(),
      );
    }
  }, [
    updateSepaDirectDebitOptionsTypeModalVisible,
    dispatch,
    updateLimitBalanceDirectDebitStatus,
    t,
  ]);

  useEffect(() => {
    if (updateStatementDirectDebitStatus === "success") {
      triggerToast(
        t("updateStatementDirectDebitStatus.success") || "",
        "valid",
      );
      dispatch(
        companiesSepaDirectDebitSlice.actions.resetUpdateStatementDirectDebitStatus(),
      );
    } else if (updateStatementDirectDebitStatus === "failed") {
      triggerToast(
        t("updateStatementDirectDebitStatus.failure") || "",
        "error",
      );
      dispatch(
        companiesSepaDirectDebitSlice.actions.resetUpdateStatementDirectDebitStatus(),
      );
    }
  }, [updateStatementDirectDebitStatus, t, dispatch]);

  useEffect(() => {
    if (activateStatementDirectDebitStatus === "success") {
      triggerToast(
        t("activateStatementDirectDebitStatus.success") || "",
        "valid",
      );
      dispatch(
        companiesSepaDirectDebitSlice.actions.resetActivateStatementDirectDebitStatus(),
      );
    } else if (activateStatementDirectDebitStatus === "failed") {
      triggerToast(
        t("activateStatementDirectDebitStatus.failure") || "",
        "error",
      );
      dispatch(
        companiesSepaDirectDebitSlice.actions.resetActivateStatementDirectDebitStatus(),
      );
    }
  }, [activateStatementDirectDebitStatus, t, dispatch]);

  useEffect(() => {
    if (activateLimitBalanceDirectDebitStatus === "success") {
      triggerToast(
        t("activateLimitBalanceDirectDebitStatus.success") || "",
        "valid",
      );
      dispatch(
        companiesSepaDirectDebitSlice.actions.resetActivateLimitBalanceDirectDebitStatus(),
      );
    } else if (activateLimitBalanceDirectDebitStatus === "failed") {
      triggerToast(
        t("activateLimitBalanceDirectDebitStatus.failure") || "",
        "error",
      );
      dispatch(
        companiesSepaDirectDebitSlice.actions.resetActivateLimitBalanceDirectDebitStatus(),
      );
    }
  }, [activateLimitBalanceDirectDebitStatus, t, dispatch]);

  useEffect(() => {
    if (updateUsageFeesDirectDebitStatus === "success") {
      triggerToast(
        t("updateUsageFeesDirectDebitStatus.success") || "",
        "valid",
      );
      dispatch(
        companiesSepaDirectDebitSlice.actions.resetUpdateUsageFeesDirectDebitStatus(),
      );
    } else if (updateUsageFeesDirectDebitStatus === "failed") {
      triggerToast(
        t("updateUsageFeesDirectDebitStatus.failure") || "",
        "error",
      );
      dispatch(
        companiesSepaDirectDebitSlice.actions.resetUpdateUsageFeesDirectDebitStatus(),
      );
    }
  }, [updateUsageFeesDirectDebitStatus, t, dispatch]);

  useEffect(() => {
    if (deactivateDirectDebitStatus === "success") {
      triggerToast(t("deactivateDirectDebitStatus.success") || "", "valid");
      dispatch(
        companiesSepaDirectDebitSlice.actions.resetDeactivateDirectDebitStatus(),
      );
    } else if (deactivateDirectDebitStatus === "failed") {
      triggerToast(t("deactivateDirectDebitStatus.failure") || "", "error");
      dispatch(
        companiesSepaDirectDebitSlice.actions.resetDeactivateDirectDebitStatus(),
      );
    }
  }, [deactivateDirectDebitStatus, t, dispatch]);

  const authorizedAdmin = useMemo(() => {
    return [
      "a.geraud@qivia.fr",
      "p.chopin@qivia.fr",
      "t.rossi@qivia.fr",
      "g.leroy@qivia.fr",
      "m.gatinois@qivia.fr",
    ].includes(adminUser?.email ?? "");
  }, [adminUser]);

  const pageName = "sepaDirectDebit";
  const apiRef = useGridApiRef();
  const key = "customers-grid";

  const aggregation = {
    model: {
      companyName: "size",
      mandateStatus: "size",
      sepaDirectDebitType: "size",
      lowerLimit: "avg",
      upperLimit: "avg",
      statementFrequency: "size",
    },
  };

  const dataGridState = usePersistMuiTableState(
    apiRef,
    key,
    pageName,
    aggregation,
  );

  const findKeysToTranslate = useCallback(
    (key: keyof SepaDirectDebitDataDisplayed, value: string | number) => {
      switch (key) {
        case "mandateStatus":
          return t(`sepaDirectDebit.mandateStatus.${value}`);
        case "sepaDirectDebitType":
          return t(`sepaDirectDebit.sepaDirectDebitType.${value}`);
        case "statementFrequency":
          return t(`sepaDirectDebit.statementFrequency.${value}`);
        default:
          return value;
      }
    },
    [t],
  );

  const keysToTranslate = useMemo(
    () =>
      [
        "mandateStatus",
        "sepaDirectDebitType",
        "statementFrequency",
      ] as (keyof SepaDirectDebitDataDisplayed)[],
    [],
  );

  const sepaDirectDebitListDisplayed: CompanySepaDirectDebitDataList[] =
    useMemo(() => {
      const rowsWithId = sepaDirectDebitList.map((av) => {
        let newDict: CompanySepaDirectDebitDataList & {
          id: string;
        } = {
          ...av,
          id: av.companyUuid,
        };
        Object.entries(av).forEach(([key, value]) => {
          if (
            keysToTranslate.includes(key as keyof SepaDirectDebitDataDisplayed)
          ) {
            newDict = {
              ...newDict,
              [key]: value
                ? findKeysToTranslate(
                    key as keyof SepaDirectDebitDataDisplayed,
                    value as string,
                  )
                : ("" as string),
            };
          }
        });
        return newDict;
      });
      return rowsWithId;
    }, [sepaDirectDebitList, findKeysToTranslate, keysToTranslate]);

  const searchBarProps = {
    values: sepaDirectDebitListDisplayed,
    setFilterValues: setListDisplayed,
    keysToIgnore: ["companyUuid" as const],
  };

  useEffect(() => {
    if (sepaDirectDebitDataStatus === "idle") {
      void dispatch(companiesSepaDirectDebitDataAsync());
    }
  }, [sepaDirectDebitDataStatus, dispatch]);

  useEffect(() => {
    if (
      sepaDirectDebitListDisplayed &&
      sepaDirectDebitDataStatus === "success"
    ) {
      const listSorted = sepaDirectDebitListDisplayed.sort((a, b) => {
        return a.companyName.localeCompare(b.companyName);
      });
      setListDisplayed(listSorted);
    }
  }, [sepaDirectDebitDataStatus, sepaDirectDebitListDisplayed]);

  const columnsDisplayed: {
    [index in keyof SepaDirectDebitDataDisplayed]: MaterialUiColumnsProps;
  } = useMemo(() => {
    return {
      companyName: {
        type: "string",
        width: 200,
      },
      mandateStatus: {
        width: 150,
        type: "string",
        valueFormatter: (value: string) => {
          return <TextCapitalized>{value}</TextCapitalized>;
        },
      },
      sepaDirectDebitType: {
        type: "string",
        valueFormatter: (value: string) => {
          return <TextCapitalized>{value}</TextCapitalized>;
        },
      },
      sepaDirectDebitUpdateType: {
        type: "string",
        width: 170,
        valueFormatter: (
          value: string | null,
          row: CompanySepaDirectDebitDataList & { id: string },
        ) => {
          if (!row["hasMemoBankDirectDebitActive"]) {
            return (
              <TextCapitalized>
                {t("sepaDirectDebit.mandat.notActivated")}
              </TextCapitalized>
            );
          }
          return (
            <StyledColumnButton>
              <ButtonDS
                singleIcon={{
                  icon: <EditOutlined />,
                  size: "L",
                }}
                format={"fill"}
                buttonType={"secondary"}
                onClick={() => {
                  setChangeSepaDirectDebitTypeModalVisible(true);
                  setCurrentSepaDirectDebitCompanyUuid(row["id"]);
                }}
              />
            </StyledColumnButton>
          );
        },
      },
      lowerLimit: {
        type: "number",
        width: 140,
      },
      upperLimit: {
        type: "number",
        width: 150,
      },
      statementFrequency: {
        type: "string",
        width: 170,
        valueFormatter: (value: string) => {
          return <TextCapitalized>{value}</TextCapitalized>;
        },
      },
      sepaDirectDebitUpdateOptionsType: {
        type: "string",
        width: 170,
        valueFormatter: (
          value: string | null,
          row: CompanySepaDirectDebitDataList & { id: string },
        ) => {
          if (
            row["sepaDirectDebitType"] ===
            t("sepaDirectDebit.sepaDirectDebitType.DEACTIVATED")
          ) {
            return (
              <TextCapitalized>
                {t("sepaDirectDebit.noSepaDirectDebit")}
              </TextCapitalized>
            );
          }
          return (
            <StyledColumnButton>
              <ButtonDS
                singleIcon={{
                  icon: <EditOutlined />,
                  size: "L",
                }}
                format={"fill"}
                buttonType={"secondary"}
                onClick={() => {
                  setUpdateSepaDirectDebitOptionsTypeModalVisible(true);
                  setCurrentSepaDirectDebitCompanyUuid(row["id"]);
                }}
              />
            </StyledColumnButton>
          );
        },
      },
      hasUsageSepaDirectDebit: {
        type: "string",
        width: 200,
        valueFormatter: (value: string | null, row) => {
          if (!row["hasMemoBankDirectDebitActive"]) {
            return (
              <TextCapitalized>
                {t("sepaDirectDebit.mandat.notActivated")}
              </TextCapitalized>
            );
          }
          return (
            <UsageFeesDirectDebitCell
              companyUuid={row["companyUuid"] as string}
              hasUsageSepaDirectDebit={
                row["hasUsageSepaDirectDebit"] as boolean
              }
            />
          );
        },
      },
      createSepaDirectDebit: {
        type: "string",
        width: 220,
        valueFormatter: (
          value: string | null,
          row: CompanySepaDirectDebitDataList & { id: string },
        ) => {
          if (!row["hasMemoBankDirectDebitActive"]) {
            return (
              <TextCapitalized>
                {t("sepaDirectDebit.mandat.notActivated")}
              </TextCapitalized>
            );
          }
          return (
            <StyledColumnButton>
              <ButtonDS
                singleIcon={{
                  icon: <SendOutlined />,
                  size: "L",
                }}
                format={"fill"}
                buttonType={"secondary"}
                onClick={() => {
                  if (!authorizedAdmin) {
                    return;
                  }
                  setCreateSepaDirectDebitModalVisible(true);
                  setCurrentSepaDirectDebitCompanyUuid(row["id"]);
                }}
                disabled={!authorizedAdmin}
              />
            </StyledColumnButton>
          );
        },
      },
    };
  }, [authorizedAdmin, t]);

  if (!listDisplayed) return <LaunchPage hasBorderRadius={true} />;

  if (
    params.tab !== "sepaDirectDebit" ||
    adminUser?.email === "exterieur1@qivia.fr"
  ) {
    return;
  }
  return (
    <StyledContainer>
      <Spacer y={1} />
      <MaterialUiTable<
        keyof SepaDirectDebitDataDisplayed,
        SepaDirectDebitDataDisplayed
      >
        rows={listDisplayed}
        columns={columnsDisplayed}
        pageName={pageName}
        dataGridState={dataGridState}
        searchBar={searchBarProps}
        apiRef={apiRef}
      />
      {currentSepaDirectDebitCompanyUuid &&
        changeSepaDirectDebitTypeModalVisible && (
          <ChangeSepaDirectDebitTypeModal
            visible={!!changeSepaDirectDebitTypeModalVisible}
            onCloseModal={() => {
              setCurrentSepaDirectDebitCompanyUuid(null);
              setChangeSepaDirectDebitTypeModalVisible(false);
            }}
            currentSepaDirectDebitCompanyUuid={
              currentSepaDirectDebitCompanyUuid
            }
          />
        )}
      {currentSepaDirectDebitCompanyUuid &&
        createSepaDirectDebitModalVisible && (
          <CreateSepaDirectDebitModal
            visible={!!createSepaDirectDebitModalVisible}
            onCloseModal={() => {
              setCurrentSepaDirectDebitCompanyUuid(null);
              setCreateSepaDirectDebitModalVisible(false);
            }}
            currentSepaDirectDebitCompanyUuid={
              currentSepaDirectDebitCompanyUuid
            }
          />
        )}
      {currentSepaDirectDebitCompanyUuid &&
        updateSepaDirectDebitOptionsTypeModalVisible && (
          <UpdateSepaDirectDebitOptionsTypeModal
            visible={!!updateSepaDirectDebitOptionsTypeModalVisible}
            onCloseModal={() => {
              setCurrentSepaDirectDebitCompanyUuid(null);
              setUpdateSepaDirectDebitOptionsTypeModalVisible(false);
            }}
            currentSepaDirectDebitCompanyUuid={
              currentSepaDirectDebitCompanyUuid
            }
          />
        )}
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const StyledColumnButton = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
`;
